// @ts-nocheck
/**
 * Cache Status Component
 * Displays real-time cache performance and sync status
 */

import React, { useState, useEffect } from 'react';
import { InlineNotification, Tag, ProgressIndicator, Tooltip } from '@carbon/react';
import { Cloud, DataBackup, CheckmarkFilled, InProgress, Warning } from '@carbon/icons-react';
import cacheService from '../../services/cacheService';
import './CacheStatus.scss';

export interface CacheStatusProps {
  userId?: string;
  showMetrics?: boolean;
  onSyncRequest?: () => void;
}

export const CacheStatus: React.FC<CacheStatusProps> = ({ userId, showMetrics = false, onSyncRequest }) => {
  const [metrics, setMetrics] = useState(cacheService.getMetrics());
  const [syncStatus, setSyncStatus] = useState<'synced' | 'syncing' | 'error'>('synced');
  const [lastSync, setLastSync] = useState<Date>(new Date());

  useEffect(() => {
    // Update metrics periodically
    const interval = setInterval(() => {
      setMetrics(cacheService.getMetrics());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Monitor sync status
    const checkSync = async () => {
      try {
        const currentMetrics = cacheService.getMetrics();
        setLastSync(currentMetrics.lastSync);

        const timeSinceSync = Date.now() - currentMetrics.lastSync.getTime();
        if (timeSinceSync > 300000) {
          // 5 minutes
          setSyncStatus('error');
        } else if (timeSinceSync > 60000) {
          // 1 minute
          setSyncStatus('syncing');
        } else {
          setSyncStatus('synced');
        }
      } catch (error) {
        setSyncStatus('error');
      }
    };

    checkSync();
    const syncInterval = setInterval(checkSync, 10000);

    return () => clearInterval(syncInterval);
  }, []);

  const handleSync = async () => {
    setSyncStatus('syncing');

    if (userId) {
      await cacheService.syncDevices(userId, 'current');
    }

    if (onSyncRequest) {
      onSyncRequest();
    }

    setSyncStatus('synced');
    setLastSync(new Date());
  };

  const getStatusIcon = () => {
    switch (syncStatus) {
      case 'synced':
        return <CheckmarkFilled size={16} className="cache-status__icon--success" />;
      case 'syncing':
        return <InProgress size={16} className="cache-status__icon--syncing" />;
      case 'error':
        return <Warning size={16} className="cache-status__icon--error" />;
    }
  };

  const getStatusColor = () => {
    switch (syncStatus) {
      case 'synced':
        return 'green';
      case 'syncing':
        return 'blue';
      case 'error':
        return 'red';
    }
  };

  const formatTime = (ms: number) => {
    if (ms < 1) return '<1ms';
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getTimeSinceSync = () => {
    const seconds = Math.floor((Date.now() - lastSync.getTime()) / 1000);

    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  return (
    <div className="cache-status" role="status" aria-live="polite">
      <div className="cache-status__indicators">
        <Tooltip label={`Cache sync status: ${syncStatus}`}>
          <Tag type={getStatusColor()} size="sm" onClick={handleSync} className="cache-status__tag">
            {getStatusIcon()}
            <span className="cache-status__label">
              {syncStatus === 'synced' ? 'Synced' : syncStatus === 'syncing' ? 'Syncing...' : 'Sync Error'}
            </span>
          </Tag>
        </Tooltip>

        {showMetrics && (
          <>
            <Tooltip label="Cache hit rate">
              <Tag type="gray" size="sm" className="cache-status__metric">
                <DataBackup size={16} />
                <span>{metrics.hitRate.toFixed(1)}%</span>
              </Tag>
            </Tooltip>

            <Tooltip label="Average load time">
              <Tag type="gray" size="sm" className="cache-status__metric">
                <Cloud size={16} />
                <span>{formatTime(metrics.avgLoadTime)}</span>
              </Tag>
            </Tooltip>

            <Tooltip label="Last sync">
              <Tag type="gray" size="sm" className="cache-status__metric">
                <span>{getTimeSinceSync()}</span>
              </Tag>
            </Tooltip>
          </>
        )}
      </div>

      {syncStatus === 'syncing' && (
        <ProgressIndicator currentIndex={1} spaceEqually className="cache-status__progress">
          <ProgressIndicator.Step label="Fetching" description="Getting latest data" complete />
          <ProgressIndicator.Step label="Syncing" description="Updating cache" current />
          <ProgressIndicator.Step label="Complete" description="Cache updated" />
        </ProgressIndicator>
      )}

      {syncStatus === 'error' && (
        <InlineNotification
          kind="warning"
          title="Sync Issue"
          subtitle="Cache may be outdated. Click to refresh."
          onCloseButtonClick={() => setSyncStatus('synced')}
          className="cache-status__notification"
        />
      )}
    </div>
  );
};
